import { NavigateFunction } from 'react-router'
import { Dispatch } from '@reduxjs/toolkit'

import { isFetchResultSuccessful } from '@/entities/FetchResult'
import {
    NotificationCenterAction,
    NotificationCenterActionCategory,
    NotificationCenterCategory,
    NotificationCenterLog,
} from '@/entities/NotificationCenter/NotificationCenter'
import {
    endEventLoggerApiCall,
    startEventLoggerApiCall,
} from '@/entities/NotificationCenter/NotificationCenterService'
import { postCommentApiCall } from '@/helpers/api/commentsApi'
import {
    cancelShiftOffer as cancelShiftOfferApiCall,
    offerShift as offerShiftApiCall,
    registerShiftOffer,
    unregisterShiftOffer,
} from '@/helpers/api/shiftsApi'
import { getShiftById, registerShift as registerShiftApiCall, unregisterShift as unregisterShiftApiCall } from '@/helpers/api-ts/shiftsApi'
import { getErrorResponse } from '@/helpers/getErrorResponse'
import { getEventLogTrackerData } from '@/helpers/getEventLogTrackerData'
import { trans } from '@/helpers/trans'
import { ExchangeResource, Shift, ShiftPlanningResource } from '@/models'
import {
    SET_LOADING,
    SHIFT_FAILURE,
    SHIFT_REQUEST,
    SHIFT_SUCCESS,
} from '@/redux/actionTypes'
import { Reducers } from '@/redux/reducers'

import { ShiftAction } from './shiftReducer'

// Shared flow of the exchange related shift actions: start an event log, call the API and go to the exchanges page
const updateExchangeAndRoute = (
    eventLogCategory: NotificationCenterCategory,
    eventLogAction: NotificationCenterAction,
    eventLogTag: NotificationCenterActionCategory,
    apiCall: (eventLogTrackerData?: NotificationCenterLog) => Promise<unknown>,
    navigate?: NavigateFunction,
) => async (dispatch: Dispatch): Promise<void> => {
    dispatch({ type: SET_LOADING, loading: true })

    const responseTracker = await startEventLoggerApiCall(eventLogCategory, eventLogAction, [eventLogTag])

    try {
        await apiCall(getEventLogTrackerData(responseTracker))

        if (navigate) {
            navigate(`/${trans('nav.exchanges.link')}`)
        }
    } catch (error) {
        dispatch({ type: SHIFT_FAILURE, errors: [getErrorResponse(error)] })
        dispatch({ type: SET_LOADING, loading: false })
    } finally {
        await endEventLoggerApiCall(getEventLogTrackerData(responseTracker))
    }
}

export const getShift = (shiftUuid: string) => async (dispatch: Dispatch): Promise<void> => {
    dispatch({ type: SET_LOADING, loading: true })
    dispatch({ type: SHIFT_REQUEST })

    try {
        const includes = [
            'department',
            'department.group',
            'tracks',
            'plannedUsers',
            'shiftPlannings',
            'shiftPlannings.comments',
            'shiftPlannings.user',
            'shiftPlannings.comments.owner',
        ]

        const response = await getShiftById(includes, shiftUuid)

        dispatch({
            type: SHIFT_SUCCESS,
            shift: response.data,
        })
    } catch (error) {
        dispatch({ type: SHIFT_FAILURE, errors: [getErrorResponse(error)] })
    }

    dispatch({ type: SET_LOADING, loading: false })
}

export const offerShift = (shiftId: string, navigate?: NavigateFunction) => updateExchangeAndRoute(
    NotificationCenterCategory.EXCHANGE,
    NotificationCenterAction.CREATED,
    NotificationCenterActionCategory.EXCHANGE_CREATED,
    (eventLogTrackerData?: NotificationCenterLog) => offerShiftApiCall(shiftId, eventLogTrackerData),
    navigate,
)

export const cancelShiftOffer = (shift: Shift, navigate?: NavigateFunction) => updateExchangeAndRoute(
    NotificationCenterCategory.EXCHANGE,
    NotificationCenterAction.DELETED,
    NotificationCenterActionCategory.EXCHANGE_CANCELLED,
    (eventLogTrackerData?: NotificationCenterLog) => cancelShiftOfferApiCall(shift, eventLogTrackerData),
    navigate,
)

export const registerOfferedShift = (exchange: ExchangeResource, navigate?: NavigateFunction) => updateExchangeAndRoute(
    NotificationCenterCategory.EXCHANGE,
    NotificationCenterAction.UPDATED,
    NotificationCenterActionCategory.EXCHANGE_UPDATED,
    (eventLogTrackerData?: NotificationCenterLog) => registerShiftOffer(exchange, eventLogTrackerData),
    navigate,
)

export const unregisterOfferedShift = (exchange: ExchangeResource, navigate?: NavigateFunction) => updateExchangeAndRoute(
    NotificationCenterCategory.EXCHANGE,
    NotificationCenterAction.UPDATED,
    NotificationCenterActionCategory.EXCHANGE_UPDATED,
    (eventLogTrackerData?: NotificationCenterLog) => unregisterShiftOffer(exchange, eventLogTrackerData),
    navigate,
)

export const setShift = (shift: Shift | null): ShiftAction => ({ type: SHIFT_SUCCESS, shift })

const openShiftsLink = (): string => `/${trans('nav.shifts.link')}/${trans('nav.shifts.openShifts.link')}`

/**
 * Posts a comment to the user's shift planning entity.
 */
const postCommentToShiftPlanning = async (
    userId: string,
    shiftId: string,
    comment: string,
    eventLogTrackerData: NotificationCenterLog,
): Promise<void> => {
    const response = await getShiftById(['shiftPlannings', 'shiftPlannings.user'], shiftId)

    const userShiftPlanning = response.data.shiftPlannings.find((shiftPlanning: ShiftPlanningResource) => shiftPlanning.user?.id === userId)

    if (! userShiftPlanning) {
        throw new Error('User shift planning not found')
    }

    await postCommentApiCall({
        comment,
        relationshipType: 'shiftPlannings',
        relationshipKey: 'shiftPlanning',
        relationshipId: userShiftPlanning.id,
        eventLogTrackerData,
    })
}

/**
 * Registers the current user for a shift and optionally posts a comment.
 */
export const registerShift = (
    shiftId: string,
    comment?: string,
    navigate?: NavigateFunction,
) => async (dispatch: Dispatch, getState: () => Reducers): Promise<void> => {
    dispatch({ type: SET_LOADING, loading: true })

    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.SHIFT_PLANNING,
        NotificationCenterAction.CREATED,
        [NotificationCenterActionCategory.SHIFT_PLANNING_REGISTERED],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch({ type: SHIFT_FAILURE, errors: [responseTracker.error] })
        return
    }

    try {
        const response = await registerShiftApiCall(shiftId, responseTracker.data)

        if (comment) {
            const userId = getState().userReducer.currentUser?.id
            if (! userId) throw new Error('No current user')

            await postCommentToShiftPlanning(userId, shiftId, comment, responseTracker.data)
        }

        // TODO: A failed registration is still handled as a success here (it was like this in the saga too)
        dispatch({
            type: SHIFT_SUCCESS,
            shift: isFetchResultSuccessful(response) ? response.data : undefined,
        })

        if (navigate) {
            navigate(openShiftsLink())
        }
    } catch (error) {
        dispatch({ type: SHIFT_FAILURE, errors: [error] })
    } finally {
        dispatch({ type: SET_LOADING, loading: false })
        await endEventLoggerApiCall(responseTracker.data)
    }
}

export const unregisterShift = (
    shiftId: string,
    navigate?: NavigateFunction,
) => async (dispatch: Dispatch): Promise<void> => {
    dispatch({ type: SET_LOADING, loading: true })

    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.SHIFT_PLANNING_REMOVAL,
        NotificationCenterAction.DELETED,
        [NotificationCenterActionCategory.SHIFT_PLANNING_REGISTRATION_CANCELLED],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch({ type: SHIFT_FAILURE, errors: [responseTracker.error] })
        return
    }

    try {
        await unregisterShiftApiCall(shiftId, responseTracker.data)

        if (navigate) {
            navigate(openShiftsLink())
        }
    } catch (error) {
        dispatch({ type: SHIFT_FAILURE, errors: [error] })
    } finally {
        dispatch({ type: SET_LOADING, loading: false })
        await endEventLoggerApiCall(responseTracker.data)
    }
}
